import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";

import { DateSelector } from "/imports/ui/components/shared";
import { FilterChip, TimeOfDayNav, MobileFilterFold, MobileFilterFoldHandle, ALL_DURATIONS } from "/imports/ui/components/booking";

// Collapsible filter chips, then the compact date strip + time-of-day nav.
// Only the date strip and nav are sticky (bleeding to the container edges,
// directly under the 64px app bar): the filters are ordinary page content, so
// once the client scrolls into the slots they scroll away instead of holding
// half the screen. They fold behind a summary row from the top, or from the
// handle under the last filter. The nav's segments double as scroll-nav for the
// slot list and follow the scroll-spy.
function LessonMobileFilterBar({ lesson, scrollSpy }) {
  const {
    navigate,
    slug,
    filters,
    setFilter,
    setSelectedSlot,
    setActiveSheet,
    bucketedSlots,
    maxBookableDate,
    suggestedGroups,
    isAuthenticated,
    canApplyPreferences,
    applyPreferences,
    selectedSport,
    selectedService,
    selectedTrainer,
    selectedLocationObj
  } = lesson;
  const { stickyRef, activeBucket, scrollToBucket } = scrollSpy;
  const location = useLocation();

  // Always starts expanded — the filters decide what the list shows, and a
  // client who never sees them books the wrong thing. Folding is opt-in.
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Pack mode and reschedule mode pin sport/service/duration; Location stays open only
  // where the client still has a choice of venue.
  const isRescheduling = !!lesson.reschedule?.active;
  const pinned = !!lesson.pack?.active || isRescheduling;
  const showPinnedLocation = isRescheduling ? lesson.serviceLocations.length > 1 : !lesson.pack?.packLocationId;

  // A lesson being moved keeps its sport, service and length — the banner above names them.
  const summary = [
    isRescheduling ? null : selectedSport?.name,
    isRescheduling ? null : selectedService?.name,
    isRescheduling ? null : filters.duration === ALL_DURATIONS ? "All durations" : filters.duration ? `${filters.duration} min` : null,
    selectedTrainer?.name || "Any trainer",
    selectedLocationObj?.name
  ]
    .filter(Boolean)
    .join(" · ");

  const goEditPreferences = () => navigate(`/${slug}/dashboard?tab=preferences&from=${encodeURIComponent(location.pathname + location.search)}`);

  return (
    <>
      <MobileFilterFold open={filtersOpen} onToggle={() => setFiltersOpen((o) => !o)} summary={summary} />

      {filtersOpen && (
        <>
          {isAuthenticated && !pinned && (
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              {canApplyPreferences ? (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: "1rem" }} />}
                    onClick={applyPreferences}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                  >
                    Use my preferences
                  </Button>
                  <IconButton
                    size="small"
                    onClick={goEditPreferences}
                    aria-label="Update preferences"
                    sx={{ flexShrink: 0, border: "1px solid", borderColor: "primary.main", borderRadius: 2, color: "primary.main", px: 1 }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<TuneIcon sx={{ fontSize: "1rem" }} />}
                  onClick={goEditPreferences}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                >
                  Set my preferences
                </Button>
              )}
            </Box>
          )}

          {/* Filter fields — fixed 2-column grid so every filter keeps a stable
              position (Sport·Location / Service·Duration / Trainer full-width) and
              long names truncate instead of pushing siblings out of view.
              Pack and reschedule modes pin sport/service/duration, so only
              Location and Trainer stay interactive. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1 }}>
            {!pinned && (
              <>
                <FilterChip label="Sport" value={selectedSport?.name || "—"} active={!!filters.sport} onClick={() => setActiveSheet("sport")} />
                <FilterChip
                  label="Location"
                  value={selectedLocationObj?.name || "All"}
                  active={!!filters.locationId}
                  onClick={() => setActiveSheet("location")}
                />
                <FilterChip label="Service" value={selectedService?.name || "—"} active onClick={() => setActiveSheet("service")} />
                <FilterChip
                  label="Duration"
                  value={filters.duration === ALL_DURATIONS ? "All durations" : filters.duration ? `${filters.duration} min` : "—"}
                  active={!!filters.duration}
                  onClick={() => setActiveSheet("duration")}
                />
              </>
            )}
            {pinned && showPinnedLocation && (
              <Box sx={{ gridColumn: isRescheduling ? "auto" : "1 / -1", minWidth: 0 }}>
                <FilterChip
                  label="Location"
                  value={selectedLocationObj?.name || "All"}
                  active={!!filters.locationId}
                  onClick={() => setActiveSheet("location")}
                />
              </Box>
            )}
            {/* Moving a lesson leaves only Location and Trainer — one row between them. */}
            <Box sx={{ gridColumn: isRescheduling && showPinnedLocation ? "auto" : "1 / -1", minWidth: 0 }}>
              <FilterChip
                label="Trainer"
                value={selectedTrainer?.name || "Any"}
                active={!!filters.trainerId}
                onClick={() => setActiveSheet("trainer")}
              />
            </Box>
          </Box>
          <MobileFilterFoldHandle onFold={() => setFiltersOpen(false)} />
        </>
      )}

      <Box
        ref={stickyRef}
        sx={{
          position: "sticky",
          top: 64,
          zIndex: 5,
          mx: -2,
          px: 2,
          pt: 1.5,
          pb: 1.25,
          bgcolor: "background.default",
          borderBottom: "1px solid",
          borderColor: "divider"
        }}
      >
        <DateSelector
          value={filters.date}
          onChange={(date) => {
            setFilter("date", date);
            setSelectedSlot(null);
          }}
          daysToShow={14}
          minDate={lesson.minBookableDate}
          hideBeforeMin
          maxDate={maxBookableDate}
          revealKey={lesson.dateRevealKey}
          compact
        />

        {/* Time-of-day nav — taps scroll to the matching section below; the
            leading ✨ pill jumps to "Suggested for you". */}
        {bucketedSlots.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <TimeOfDayNav buckets={bucketedSlots} activeBucket={activeBucket} onBucketTap={scrollToBucket} suggestedCount={suggestedGroups.length} />
          </Box>
        )}
      </Box>
    </>
  );
}

export default LessonMobileFilterBar;
